import React, { useMemo } from 'react';

import { TableCell, TableRow } from '@material-ui/core';
import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import { useSortedItems } from 'hooks/useSorting';
import PropTypes from 'prop-types';

import { BasicTable } from 'components/DebFormComponents/Table/BasicTable';
import { CustomTableHeader } from 'components/DebFormComponents/Table/CustomTableHeader';
import { ExpertTableInfoText } from 'components/ExpertTableInfoText/ExpertTableInfoText';
import { FormSection } from 'components/FormHelper/FormSection';
import { NEFI_ERROR } from 'components/NEFIForm/Utils/NEFIError';
import { TooltipIconButton } from 'components/TooltipIconButton/TooltipIconButton';

import { areExpertContactsEnabled } from 'utils/FormUtils';

import { CustomTableCell } from './CustomTableCell';

import './NEFIExpertsTable.sass';

const buildHeaders = (hasEnergy, hasGas, updateSorting, sortingObject) => {
  const headers = [
    <CustomTableHeader
      title="Name / Kontakt"
      updateKey="completeName"
      sortingObject={sortingObject}
      key="Name / Kontakt"
      updateSorting={updateSorting}
    />,
  ];

  if (hasEnergy) {
    headers.push(
      <CustomTableHeader
        title="Strom"
        updateKey="isExpertEnergyActive"
        sortingObject={sortingObject}
        key="Strom"
        updateSorting={updateSorting}
        sorting={false}
      />
    );
  }

  if (hasGas) {
    headers.push(
      <CustomTableHeader
        title="Gas"
        updateKey="isExpertGasActive"
        sortingObject={sortingObject}
        key="Gas"
        updateSorting={updateSorting}
        sorting={false}
      />
    );
  }

  headers.push(
    <CustomTableHeader
      title=""
      updateKey="Button"
      sortingObject={sortingObject}
      key="Button"
      updateSorting={updateSorting}
      sorting={false}
    />
  );

  return headers;
};

export const NEFIExpertTable = ({ formik, branchSelectionValues, errors }) => {
  const deleteExpert = (id) => {
    const newExperts = formik.values.experts.filter((expert) => expert.id !== id);
    formik.setFieldValue('experts', newExperts);
  };

  const expertsWithSortName = useMemo(
    () =>
      formik.values.experts.map((expert) => ({
        ...expert,
        completeName:
          expert.completeName || `${expert.lastName || ''} ${expert.firstName || ''}`.trim(),
      })),
    [formik.values.experts]
  );

  const [sortedExperts, sortConfig, , updateSorting] = useSortedItems(expertsWithSortName);

  const rows = useMemo(
    () =>
      sortedExperts.map((row) => (
        <TableRow
          key={row.id}
          sx={{
            '&:last-child td, &:last-child th': { border: 0 },
          }}>
          <TableCell scope="row" key="name" className="data expert-table__name-cell">
            <div className="expert-table__cell-container">
              <div className="expert-table__name-block">
                <span className="full-name">
                  {`${row.lastName || ''} ${row.firstName || ''}`.trim()}
                </span>
                {areExpertContactsEnabled && row.telefon && (
                  <span className="contact-line">{row.telefon}</span>
                )}
                {areExpertContactsEnabled && row.netzportalEmail && (
                  <span className="contact-line">{row.netzportalEmail}</span>
                )}
              </div>
            </div>
          </TableCell>
          {branchSelectionValues.isEnergyActive ? (
            <CustomTableCell align="left" key="isExpertEnergyActive" className="data">
              {row.isExpertEnergyActive}
            </CustomTableCell>
          ) : null}
          {branchSelectionValues.isGasActive ? (
            <CustomTableCell align="left" key="isExpertGasActive" className="data">
              {row.isExpertGasActive}
            </CustomTableCell>
          ) : null}
          <TableCell align="center" key="deleteButton" className="action-buttons">
            <div className="expert-table__button-container">
              <TooltipIconButton
                className="expert-table__icon-button"
                aria-label="Fachinteressent löschen"
                onClick={() => {
                  deleteExpert(row.id);
                }}>
                <DeleteIcon />
              </TooltipIconButton>
            </div>
          </TableCell>
        </TableRow>
      )),
    [sortedExperts, branchSelectionValues.isEnergyActive, branchSelectionValues.isGasActive]
  );

  const showInfoText = useMemo(() => {
    if (formik.values.experts.length === 0) return true;

    const hasEnergyExpert = formik.values.experts.some((expert) => expert.isExpertEnergyActive);
    const hasGasExpert = formik.values.experts.some((expert) => expert.isExpertGasActive);

    const needsEnergyExpert = branchSelectionValues.isEnergyActive && !hasEnergyExpert;
    const needsGasExpert = branchSelectionValues.isGasActive && !hasGasExpert;

    return needsEnergyExpert || needsGasExpert;
  }, [
    formik.values.experts,
    branchSelectionValues.isEnergyActive,
    branchSelectionValues.isGasActive,
  ]);

  return (
    <div className="expert-table__wrapper">
      <FormSection
        infoText={
          <ExpertTableInfoText
            showInfoText={showInfoText}
            hasEnergy={branchSelectionValues.isEnergyActive}
            hasGas={branchSelectionValues.isGasActive}
            errors={errors}
            errorTypes={[NEFI_ERROR.MIN_EXPERT, NEFI_ERROR.MIN_BRANCH_EXPERT]}
            primaryErrorClass="nefi-form__error-text"
            secondaryErrorClass={
              errors.includes(NEFI_ERROR.MIN_BRANCH_EXPERT) ? 'nefi-form__error-text' : ''
            }
            expertLabel="Fachinteressent"
          />
        }>
        <BasicTable
          className="expert-table"
          headers={buildHeaders(
            branchSelectionValues.isEnergyActive,
            branchSelectionValues.isGasActive,
            updateSorting,
            sortConfig
          )}
          rows={rows}
        />
      </FormSection>
    </div>
  );
};

NEFIExpertTable.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.oneOf([Object.values(NEFI_ERROR)])).isRequired,
  formik: PropTypes.object.isRequired,
  branchSelectionValues: PropTypes.object.isRequired,
};
